import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSocietyDetails,
  getStateAndCity,
  claimExtraCharges
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { multipleUploadFiles } from '../../features/files';

const ClaimExtraChargesForm = ({ isOpen, onClose, dealId, onSuccess, projectId }) => {
  // Redux
  const dispatch = useDispatch();
  const fileUrls = useSelector((state) => state.file.fileUrls);
  const MultiFiles = useSelector((state) => state.file.multifiles);

  // React Hook Form
  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  // Claim Extra Charges modal state
  const [gstPercentage, setGstPercentage] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [type, setType] = useState('Builder'); // Default to Builder
  const [societyDetails, setSocietyDetails] = useState(null);
  const [societyFormData, setSocietyFormData] = useState({
    society_name: '',
    gst: '',
    pan: '',
    zip_code: '',
    city: '',
    state: '',
    country: ''
  });
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);

  // Format files helper function (same as EditProject)
  const formatFiles = (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      return [];
    }
    return files.map((file) => ({
      url: file.url || '',
      name: file.name || ''
    }));
  };

  // Handle type change
  const handleTypeChange = async (selectedType) => {
    setType(selectedType);

    // If Society is selected, call API to get details
    if (selectedType === 'Society') {
      try {
        const response = await getSocietyDetails(projectId);
        setSocietyDetails(response);

        if (response && Array.isArray(response) && response.length > 0) {
          // Society details exist, prefill the form with the first society
          const societyData = response[0];
          setSocietyFormData({
            society_name: societyData.name || '',
            gst: societyData.gst || '',
            pan: societyData.pan || '',
            zip_code: societyData.zip_code || '',
            city: societyData.city || '',
            state: societyData.state || '',
            country: societyData.country || ''
          });

          // Prefill logo files in Redux state
          if (societyData.logo && Array.isArray(societyData.logo) && societyData.logo.length > 0) {
            dispatch(
              multipleUploadFiles({
                fileType: 'society_logo',
                urls: societyData.logo.map((logo) => ({
                  url: logo.url,
                  name: logo.name
                }))
              })
            );
          }

          toast.success('Society details found and prefilled');
        } else {
          // No society details, reset form for user input
          setSocietyFormData({
            society_name: '',
            gst: '',
            pan: '',
            zip_code: '',
            city: '',
            state: '',
            country: ''
          });

          // Clear logo files from Redux state
          dispatch(
            multipleUploadFiles({
              fileType: 'society_logo',
              urls: []
            })
          );

          toast.info('No society details found. Please fill the form.');
        }
      } catch (error) {
        // Reset form on error
        setSocietyFormData({
          society_name: '',
          gst: '',
          pan: '',
          zip_code: '',
          city: '',
          state: '',
          country: ''
        });

        // Clear logo files from Redux state
        dispatch(
          multipleUploadFiles({
            fileType: 'society_logo',
            urls: []
          })
        );
      }
    } else {
      // Builder selected, clear society details
      setSocietyDetails(null);
      setSocietyFormData({
        society_name: '',
        gst: '',
        pan: '',
        zip_code: '',
        city: '',
        state: '',
        country: ''
      });

      // Clear logo files from Redux state
      dispatch(
        multipleUploadFiles({
          fileType: 'society_logo',
          urls: []
        })
      );
    }
  };

  // Handle society form data change
  const handleSocietyFormChange = (field, value) => {
    setSocietyFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle pincode change for auto-filling city, state, and country
  const handlePincodeChange = useCallback(async (pincode) => {
    const cleanedPincode = pincode.replace(/[^0-9]/g, '');

    // Update zip_code in form data
    setSocietyFormData((prev) => ({
      ...prev,
      zip_code: cleanedPincode
    }));

    if (cleanedPincode.length === 6) {
      try {
        const pincodeList = await getStateAndCity(cleanedPincode);
        const postOfficeList = pincodeList[0].PostOffice;

        if (postOfficeList.length > 0) {
          const newState = postOfficeList[0].State;
          const uniqueCircles = [
            ...new Set(
              postOfficeList
                .map((office) => {
                  const circle =
                    office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                      ? office.Name
                      : office.Block;
                  return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                })
                .filter(Boolean)
            )
          ];

          // Set dropdown options for city selection
          const cityOptions = uniqueCircles.map((circle) => ({
            value: circle,
            label: circle
          }));
          setCityDropdownOptions(cityOptions);

          // Auto-select first city if available
          const newCity = uniqueCircles[0];

          // Auto-fill city, state, and country
          setSocietyFormData((prev) => ({
            ...prev,
            city: newCity || '',
            state: newState || '',
            country: 'India'
          }));

          if (uniqueCircles.length > 1) {
            toast.success(`Found ${uniqueCircles.length} cities. Please select from dropdown.`);
          } else {
            toast.success('City, state, and country auto-filled from pincode');
          }
        } else {
          // Reset if no data found
          setSocietyFormData((prev) => ({
            ...prev,
            city: '',
            state: '',
            country: ''
          }));
          setCityDropdownOptions([]);
          toast.info('No location data found for this pincode');
        }
      } catch (error) {
        console.error('Error fetching location data:', error);
        toast.error(
          error?.response?.data?.message || error?.message || 'Error fetching location data'
        );
        // Reset on error
        setSocietyFormData((prev) => ({
          ...prev,
          city: '',
          state: '',
          country: ''
        }));
        setCityDropdownOptions([]);
      }
    }
  }, []);

  // Submit claim extra charges
  const handleSubmitClaimExtraCharges = async () => {
    if (!gstPercentage) {
      toast.error('Please enter GST percentage');
      return;
    }

    if (!invoiceNumber) {
      toast.error('Please enter Invoice Number');
      return;
    }

    if (!issueDate) {
      toast.error('Please select Issue Date');
      return;
    }

    // If Society is selected, validate required fields
    if (type === 'Society') {
      const requiredFields = ['society_name', 'gst', 'pan'];
      const missingFields = requiredFields.filter((field) => !societyFormData[field]);

      if (missingFields.length > 0) {
        toast.error(`Please fill required fields: ${missingFields.join(', ')}`);
        return;
      }
    }

    // Prepare payload according to the new structure
    const payload = {
      deal_id: dealId,
      source: 'Extra Charge',
      gst_percentage: parseFloat(gstPercentage),
      type: type,
      invoiceNumber: invoiceNumber,
      issueDate: issueDate
    };

    // Add society details if type is Society
    if (type === 'Society') {
      payload.society_name = societyFormData.society_name;
      payload.gst = societyFormData.gst;
      payload.pan = societyFormData.pan;
      payload.zip_code = societyFormData.zip_code;
      payload.city = societyFormData.city;
      payload.state = societyFormData.state;
      payload.country = societyFormData.country;

      // Handle logo - use Redux state and formatFiles
      payload.logo = formatFiles(MultiFiles['society_logo'] || []);
    }

    console.log('Claim Extra Charges Payload:', payload);
    try {
      const response = await claimExtraCharges(payload);
      console.log('Claim Extra Charges Response:', response);
      toast.success('Claim Extra Charges submitted successfully');
      onSuccess();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Error claiming extra charges'
      );
    } finally {
      resetForm();
      onClose();
    }
  };

  // Reset form
  const resetForm = () => {
    setGstPercentage('');
    setInvoiceNumber('');
    setIssueDate('');
    setType('Builder');
    setSocietyDetails(null);
    setSocietyFormData({
      society_name: '',
      gst: '',
      pan: '',
      zip_code: '',
      city: '',
      state: '',
      country: ''
    });
    setCityDropdownOptions([]);

    // Clear logo files from Redux state
    dispatch(
      multipleUploadFiles({
        fileType: 'society_logo',
        urls: []
      })
    );

    onClose();
  };

  return (
    <Modal
      isDirty={true}
      confirmOnClose={false}
      name={'Claim Extra Charges'}
      onClose={resetForm}
      width={50}>
      <div className="space-y-6">
        {/* First row: GST Percentage and Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GST % <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={gstPercentage}
              onChange={(e) => setGstPercentage(e.target.value)}
              onInput={(e) => {
                let value = e.target.value;
                value = value.replace(/[^0-9.]/g, '');
                e.target.value = value;
              }}
              placeholder="Enter GST percentage"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              maxLength={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required>
              <option value="Builder">Builder</option>
              <option value="Society">Society</option>
            </select>
          </div>
        </div>

        {/* Second row: Invoice Number and Issue Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Enter invoice number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Society Details Section - Only show when Society is selected */}
        {type === 'Society' && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Society Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Society Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={societyFormData.society_name}
                  onChange={(e) => handleSocietyFormChange('society_name', e.target.value)}
                  placeholder="Enter society name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={societyFormData.gst}
                  onChange={(e) => handleSocietyFormChange('gst', e.target.value)}
                  placeholder="Enter GST number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={societyFormData.pan}
                  onChange={(e) => handleSocietyFormChange('pan', e.target.value)}
                  placeholder="Enter PAN number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                <input
                  type="text"
                  value={societyFormData.zip_code}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  placeholder="Enter 6-digit pincode"
                  maxLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                {cityDropdownOptions.length > 0 ? (
                  <select
                    value={societyFormData.city}
                    onChange={(e) => handleSocietyFormChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select City</option>
                    {cityDropdownOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={societyFormData.city}
                    onChange={(e) => handleSocietyFormChange('city', e.target.value)}
                    placeholder="Enter city"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={societyFormData.state}
                  onChange={(e) => handleSocietyFormChange('state', e.target.value)}
                  placeholder="Enter state"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  value={societyFormData.country}
                  onChange={(e) => handleSocietyFormChange('country', e.target.value)}
                  placeholder="Enter country"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={true}
                />
              </div>

              <FormInput
                width={100}
                register={register}
                register_key={'society_logo'}
                label={'Society Logo'}
                type={'file-secondary'}
                prefilledFiles={MultiFiles['society_logo'] || []}
                accept={'.pdf, .png, .jpg, .jpeg'}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 pt-6 border-t">
          <Button type="button" onClick={resetForm} displayType="secondary">
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmitClaimExtraCharges}>
            Submit Claim
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ClaimExtraChargesForm;
